import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Calendar, Store, AlertCircle, AlertTriangle, TrendingUp, Settings, Trophy, Edit3, CheckCircle, Sun, Cloud, CloudRain, CloudLightning, Snowflake, CloudFog, ShoppingBag, Printer, X, ChevronRight, CheckSquare, Users, DollarSign, Menu, LogOut, ChevronDown, ChevronUp } from 'lucide-react';
import Layout from './Layout';
import { useStore } from '../context/StoreContext';
import './Dashboard.css';

const KBO_TEAMS = [
    'LG 트윈스', '삼성 라이온즈', 'SSG 랜더스', 'NC 다이노스', '두산 베어스',
    'KIA 타이거즈', '롯데 자이언츠', '한화 이글스', '키움 히어로즈', 'KT 위즈'
];

const DEFAULT_ITEMS = ['핫도그 번', '소시지 (10개입)', '생수 500ml', '펩시 콜라 355ml', '종이컵 (L)', '케첩/머스타드 (팩)', '종이 트레이'];

const Dashboard = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { stores, user, currentDate, eventHistory, updateEvent, attendanceHistory, updateAttendance, selectedStoreId, setSelectedStoreId } = useStore();
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const queryDate = queryParams.get('date');
    const initialDate = queryDate || currentDate;
    const [initY, initM, initD] = initialDate ? initialDate.split('-') : ['', '', ''];

    const [selectedDate, setSelectedDate] = useState({
        year: initY,
        month: initM,
        day: initD
    });
    const [weather, setWeather] = useState({ temp: null, code: null, loading: false });
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [drillDownModal, setDrillDownModal] = useState({ isOpen: false, title: '', stores: [], type: '' });
    const [filterType, setFilterType] = useState('all'); // 'all', 'direct', 'rental'

    useEffect(() => {
        // 모든 사용자(관리자 & 매니저)에 대해 URL의 date 파라미터가 있으면 우선 적용
        if (queryDate) {
            const [y, m, d] = queryDate.split('-');
            if (y !== selectedDate.year || m !== selectedDate.month || d !== selectedDate.day) {
                setSelectedDate({ year: y, month: m, day: d });
            }
        } else if (currentDate && (!selectedDate.year || !selectedDate.month || !selectedDate.day)) {
            const [y, m, d] = currentDate.split('-');
            setSelectedDate({ year: y, month: m, day: d });
        }
    }, [queryDate, currentDate, selectedDate.year, selectedDate.month, selectedDate.day]);

    // 로그인 정보가 없으면 로그인 페이지로 이동 (기본 보안)
    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        // 매니저가 모바일에서 접속하면 설정 페이지로 자동 이동 (첫 화면)
        // ※ sessionStorage에 'settingsDone' 플래그가 있으면 이미 설정 완료 → 리다이렉트 안 함
        const settingsDone = sessionStorage.getItem(`settingsDone_${user.storeId}`);
        if (user.role === 'manager' && window.innerWidth < 1260 && !settingsDone) {
            navigate(`/store/${user.storeId}/mobile-settings`, { replace: true });
        }
    }, [user, navigate]);

    // 날씨 정보 가져오기
    useEffect(() => {
        const fetchWeather = async () => {
            if (!selectedDate.year || !selectedDate.month || !selectedDate.day) return;

            setWeather(prev => ({ ...prev, loading: true }));
            const dateStr = `${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`;

            try {
                // Open-Meteo API 사용 (서울 좌표: 37.5665, 126.9780)
                const url = `https://archive-api.open-meteo.com/v1/archive?latitude=37.5665&longitude=126.9780&start_date=${dateStr}&end_date=${dateStr}&daily=weathercode,temperature_2m_max&timezone=Asia%2FSeoul`;

                // 만약 미래 날짜라면 forecast API 사용
                const today = new Date();
                const selected = new Date(dateStr);
                const isFuture = selected > today;

                const finalUrl = isFuture
                    ? `https://api.open-meteo.com/v1/forecast?latitude=37.5665&longitude=126.9780&daily=weathercode,temperature_2m_max&timezone=Asia%2FSeoul&start_date=${dateStr}&end_date=${dateStr}`
                    : url;

                const response = await fetch(finalUrl);
                const data = await response.json();

                if (data.daily) {
                    setWeather({
                        temp: data.daily.temperature_2m_max[0],
                        code: data.daily.weathercode[0],
                        loading: false
                    });
                } else {
                    setWeather(prev => ({ ...prev, loading: false }));
                }
            } catch (error) {
                console.error('Weather fetch error:', error);
                setWeather(prev => ({ ...prev, loading: false }));
            }
        };

        fetchWeather();
    }, [selectedDate]);

    const getWeatherDisplay = (code) => {
        // WMO Weather interpretation codes (WW)
        if (code === null || code === undefined) return { icon: <Sun size={16} />, text: '맑음' };
        if (code <= 1) return { icon: <Sun size={16} color="#fbbf24" />, text: '맑음' };
        if (code <= 3) return { icon: <Cloud size={16} color="#94a3b8" />, text: '구름' };
        if (code <= 48) return { icon: <CloudFog size={16} color="#94a3b8" />, text: '안개' };
        if (code <= 67) return { icon: <CloudRain size={16} color="#38bdf8" />, text: '비' };
        if (code <= 77) return { icon: <Snowflake size={16} color="#f8fafc" />, text: '눈' };
        if (code <= 82) return { icon: <CloudRain size={16} color="#38bdf8" />, text: '소나기' };
        if (code <= 86) return { icon: <Snowflake size={16} color="#f8fafc" />, text: '폭설' };
        return { icon: <CloudLightning size={16} color="#fbbf24" />, text: '뇌우' };
    };

    const allowedStores = useMemo(() => {
        if (!stores) return [];
        const restrictedAdmins = ['admin1', 'admin2', 'admin3'];
        if (user?.role === 'admin' && restrictedAdmins.includes(user?.id?.toLowerCase())) {
            return stores.filter(s => s.operatingType === 'direct');
        }
        return stores;
    }, [stores, user]);

    // 권한에 따른 매장 필터링
    const filteredStores = useMemo(() => {
        if (!user) return [];
        let result = allowedStores;

        // 1. 운영 형태 필터링 (직영/임대) - 관리자 계정에서 전체 매장 보기 시에만 적용
        if (filterType !== 'all' && user.role === 'admin' && selectedStoreId === 'all') {
            result = result.filter(s => s.operatingType === filterType);
        }

        // 2. 권한 및 선택 매장 필터링
        if (user.role === 'admin') {
            if (selectedStoreId === 'all') return result;
            return result.filter(s => s.id === selectedStoreId);
        }
        return result.filter(s => s.id === user.storeId);
    }, [allowedStores, user, selectedStoreId, filterType]);

    // 집계 데이터 계산
    const stats = useMemo(() => {
        if (!selectedDate.year || !selectedDate.month || !selectedDate.day) {
            return {
                totalDaySales: 0, totalPrev1Sales: 0, totalPrev2Sales: 0, totalPrev3Sales: 0,
                totalMonthSales: 0, totalYearSales: 0, directDaySales: 0, rentalDaySales: 0,
                directMonthSales: 0, rentalMonthSales: 0, directYearSales: 0, rentalYearSales: 0,
                totalDayVariance: 0, totalMonthVariance: 0, totalYearVariance: 0, activeStores: 0,
                totalCount: filteredStores.length, shortageCount: 0, totalShortageItems: 0,
                malfunctionCount: 0, totalMalfunctionItems: 0, orderedCount: 0,
                shortageStores: [], malfunctionStores: [], orderedStores: [],
                completedStoresList: [], pendingStoresList: []
            };
        }

        let totalDaySales = 0, totalPrev1Sales = 0, totalPrev2Sales = 0, totalPrev3Sales = 0;
        let totalMonthSales = 0, totalYearSales = 0, directDaySales = 0, rentalDaySales = 0;
        let directMonthSales = 0, rentalMonthSales = 0, directYearSales = 0, rentalYearSales = 0;
        let totalDayVariance = 0, totalMonthVariance = 0, totalYearVariance = 0;
        
        // 날짜 정규화 헬퍼 (YYYY-MM-DD 보장)
        const normalizeDate = (dStr) => {
            if (!dStr) return '';
            const dt = dStr.split('T')[0];
            const [y, m, d] = dt.split('-');
            if (!y || !m || !d) return dt;
            return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
        };

        const todayStr = `${selectedDate.year}-${String(selectedDate.month).padStart(2, '0')}-${String(selectedDate.day).padStart(2, '0')}`;
        const monthPrefix = `${selectedDate.year}-${String(selectedDate.month).padStart(2, '0')}`;
        const yearPrefix = `${selectedDate.year}`;
        
        const baseDate = new Date(selectedDate.year, selectedDate.month - 1, selectedDate.day);
        const getOffsetDateStr = (offsetDays) => {
            const d = new Date(baseDate);
            d.setDate(d.getDate() - offsetDays);
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        };
        const prev1Str = getOffsetDateStr(1);
        const prev2Str = getOffsetDateStr(2);
        const prev3Str = getOffsetDateStr(3);

        let activeStores = 0, shortageStoresCount = 0, malfunctionStoresCount = 0;
        let totalShortageItems = 0, totalMalfunctionItems = 0, orderedCount = 0;
        const shortageStores = [], malfunctionStores = [], orderedStores = [];
        const completedStoresList = [], pendingStoresList = [];
        const globalDailySales = new Map();

        filteredStores.forEach(store => {
            const isDirect = store.operatingType === 'direct';
            const recordsMap = new Map();
            (store.salesData || []).forEach(item => {
                if (!item) return;
                const d = normalizeDate(item.localDate || item.date);
                if (d) recordsMap.set(d, item);
            });
            Object.entries(store.salesHistory || {}).forEach(([dateKey, record]) => {
                const innerD = normalizeDate(dateKey || record.localDate || record.date?.split('T')[0]);
                if (innerD) recordsMap.set(innerD, record);
            });
            const sortedDates = Array.from(recordsMap.keys()).sort((a, b) => b.localeCompare(a));
            
            const dayRecord = recordsMap.get(todayStr);
            if (dayRecord) {
                const total = Number(dayRecord.total) || (Number(dayRecord.cash || 0) + Number(dayRecord.card || 0));
                totalDaySales += total;
                totalDayVariance += Number(dayRecord.variance) || 0;
                if (isDirect) directDaySales += total; else rentalDaySales += total;
            }

            const pastDates = sortedDates.filter(d => d < todayStr);
            
            const r1 = recordsMap.get(prev1Str);
            if (r1) totalPrev1Sales += Number(r1.total) || (Number(r1.cash || 0) + Number(r1.card || 0));
            
            const r2 = recordsMap.get(prev2Str);
            if (r2) totalPrev2Sales += Number(r2.total) || (Number(r2.cash || 0) + Number(r2.card || 0));
            
            const r3 = recordsMap.get(prev3Str);
            if (r3) totalPrev3Sales += Number(r3.total) || (Number(r3.cash || 0) + Number(r3.card || 0));

            recordsMap.forEach((record, d) => {
                const total = Number(record.total) || (Number(record.cash || 0) + Number(record.card || 0));
                globalDailySales.set(d, (globalDailySales.get(d) || 0) + total);
                const variance = Number(record.variance) || 0;
                if (d.startsWith(monthPrefix) && d <= todayStr) {
                    totalMonthSales += total; totalMonthVariance += variance;
                    if (isDirect) directMonthSales += total; else rentalMonthSales += total;
                }
                if (d.startsWith(yearPrefix) && d <= todayStr) {
                    totalYearSales += total; totalYearVariance += variance;
                    if (isDirect) directYearSales += total; else rentalYearSales += total;
                }
            });

            const status = store.dailyStatus?.[todayStr];
            const isDone = status?.sales && status?.inventory && status?.facility && status?.order;
            if (isDone) { activeStores++; completedStoresList.push(store); } else { pendingStoresList.push(store); }

            const invRecord = store.inventoryHistory?.[todayStr] || (pastDates.length > 0 ? store.inventoryHistory?.[pastDates[0]] : null);
            const stocks = invRecord?.stocks || store.inventoryData?.stocks || [];
            const itemNames = (invRecord?.itemNames && invRecord.itemNames.length > 0) ? invRecord.itemNames : ((store.items && store.items.length > 0) ? store.items : DEFAULT_ITEMS);
            
            // 전수 조사: 매장의 전체 품목(itemNames)을 기준으로 재고가 1개 미만(0 또는 누락)인 경우를 체크
            const sCount = itemNames.filter((name, idx) => {
                // 이름을 지웠더라도 기본 7개 항목에 포함되므로 카운트에서 누락되지 않도록 주석 처리
                // if (!name || name.trim() === '') return false;
                const s = stocks[idx];
                const val = (s !== undefined && s !== null && s !== '' ? Number(s) : 0);
                return val < 1;
            }).length;

            if (sCount > 0) { shortageStoresCount++; totalShortageItems += sCount; shortageStores.push({ ...store, count: sCount }); }

            const facRecord = store.facilityHistory?.[todayStr] || (pastDates.length > 0 ? store.facilityHistory?.[pastDates[0]] : null);
            if (facRecord) {
                const mCount = (facRecord.items || []).filter(f => f.name && f.name.trim() !== '' && f.status && f.status !== 'normal').length;
                if (mCount > 0) { malfunctionStoresCount++; totalMalfunctionItems += mCount; malfunctionStores.push({ ...store, count: mCount }); }
            }

            const orderRecord = store.orderHistory?.[todayStr];
            if (orderRecord?.stocks?.some(s => Number(s) > 0)) {
                orderedCount++;
                orderedStores.push({ ...store, updatedAt: orderRecord.updatedAt, items: store.items.map((name, idx) => ({ name, qty: Number(orderRecord.stocks[idx]) || 0 })).filter(i => i.qty > 0) });
            }
        });

        // 글로벌 과거 기준 데이터 추출
        const sortedGlobalDates = Array.from(globalDailySales.keys()).sort((a, b) => b.localeCompare(a));
        const getGlobalPastSalesBefore = (targetDateStr) => {
            for (const d of sortedGlobalDates) {
                if (d < targetDateStr) {
                    const t = globalDailySales.get(d) || 0;
                    if (t > 0) return t;
                }
            }
            return null;
        };

        const totalPastSalesForDay = getGlobalPastSalesBefore(todayStr);
        const totalPastSalesForPrev1 = getGlobalPastSalesBefore(prev1Str);
        const totalPastSalesForPrev2 = getGlobalPastSalesBefore(prev2Str);

        const totalDaySalesChange = totalPastSalesForDay !== null ? ((totalDaySales - totalPastSalesForDay) / totalPastSalesForDay) * 100 : null;
        const totalPrev1SalesChange = totalPastSalesForPrev1 !== null && totalPrev1Sales > 0 ? ((totalPrev1Sales - totalPastSalesForPrev1) / totalPastSalesForPrev1) * 100 : null;
        const totalPrev2SalesChange = totalPastSalesForPrev2 !== null && totalPrev2Sales > 0 ? ((totalPrev2Sales - totalPastSalesForPrev2) / totalPastSalesForPrev2) * 100 : null;

        return {
            totalDaySales, totalPrev1Sales, totalPrev2Sales, totalPrev3Sales, totalMonthSales, totalYearSales,
            totalPastSalesForDay, totalPastSalesForPrev1, totalPastSalesForPrev2,
            totalDaySalesChange, totalPrev1SalesChange, totalPrev2SalesChange,
            directDaySales, rentalDaySales, directMonthSales, rentalMonthSales, directYearSales, rentalYearSales,
            totalDayVariance, totalMonthVariance, totalYearVariance, activeStores,
            totalCount: filteredStores.length, shortageCount: shortageStoresCount, totalShortageItems,
            malfunctionCount: malfunctionStoresCount, totalMalfunctionItems, orderedCount,
            shortageStores, malfunctionStores, orderedStores, completedStoresList, pendingStoresList
        };
    }, [stores, filteredStores, selectedDate]);

    // 매장별 상세 데이터를 미리 계산 (성능 최적화)
    const storeDetails = useMemo(() => {
        if (!selectedDate.year || !selectedDate.month || !selectedDate.day) {
            return [];
        }

        // 날짜 정규화 헬퍼 (YYYY-MM-DD 보장)
        const normalizeDate = (dStr) => {
            if (!dStr) return '';
            const dt = dStr.split('T')[0];
            const [y, m, d] = dt.split('-');
            if (!y || !m || !d) return dt;
            return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
        };

        const todayStr = `${selectedDate.year}-${String(selectedDate.month).padStart(2, '0')}-${String(selectedDate.day).padStart(2, '0')}`;
        const monthPrefix = `${selectedDate.year}-${String(selectedDate.month).padStart(2, '0')}`;
        const yearPrefix = `${selectedDate.year}`;
        
        const baseDate = new Date(selectedDate.year, selectedDate.month - 1, selectedDate.day);
        const getOffsetDateStr = (offsetDays) => {
            const d = new Date(baseDate);
            d.setDate(d.getDate() - offsetDays);
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        };
        const prev1Str = getOffsetDateStr(1);
        const prev2Str = getOffsetDateStr(2);
        const prev3Str = getOffsetDateStr(3);

        return filteredStores.map(store => {
            const recordsMap = new Map();
            (store.salesData || []).forEach(item => {
                if (!item) return;
                const d = normalizeDate(item.localDate || item.date);
                if (d) recordsMap.set(d, item);
            });
            Object.entries(store.salesHistory || {}).forEach(([dateKey, record]) => {
                const innerD = normalizeDate(dateKey || record.localDate || record.date?.split('T')[0]);
                if (innerD) recordsMap.set(innerD, record);
            });

            const sortedDates = Array.from(recordsMap.keys()).sort((a, b) => b.localeCompare(a));
            
            let daySales = 0;
            let prev1Sales = 0;
            let prev2Sales = 0;
            let prev3Sales = 0;
            let monthSales = 0;
            let yearSales = 0;
            let yearVariance = 0;

            const dayRecord = recordsMap.get(todayStr);
            if (dayRecord) daySales = Number(dayRecord.total) || (Number(dayRecord.cash || 0) + Number(dayRecord.card || 0));

            const pastDates = sortedDates.filter(d => d < todayStr);
            
            const r1 = recordsMap.get(prev1Str);
            prev1Sales = r1 ? (Number(r1.total) || (Number(r1.cash || 0) + Number(r1.card || 0))) : 0;

            const r2 = recordsMap.get(prev2Str);
            prev2Sales = r2 ? (Number(r2.total) || (Number(r2.cash || 0) + Number(r2.card || 0))) : 0;

            const r3 = recordsMap.get(prev3Str);
            prev3Sales = r3 ? (Number(r3.total) || (Number(r3.cash || 0) + Number(r3.card || 0))) : 0;

            recordsMap.forEach((record, d) => {
                const total = Number(record.total) || (Number(record.cash || 0) + Number(record.card || 0));
                if (d.startsWith(monthPrefix) && d <= todayStr) monthSales += total;
                if (d.startsWith(yearPrefix) && d <= todayStr) {
                    yearSales += total;
                    yearVariance += Number(record.variance) || 0;
                }
            });

            const status = store.dailyStatus?.[todayStr];
            const isDone = status?.sales && status?.inventory && status?.facility && status?.order;

            // 재고 부족 - 가장 최근 데이터 사용
            const invRecord = store.inventoryHistory?.[todayStr] || (pastDates.length > 0 ? store.inventoryHistory?.[pastDates[0]] : null);
            let stocks = invRecord?.stocks || store.inventoryData?.stocks || [];
            let itemNames = (invRecord?.itemNames && invRecord.itemNames.length > 0) ? invRecord.itemNames : ((store.items && store.items.length > 0) ? store.items : DEFAULT_ITEMS);
            const shortageCount = itemNames.filter((name, idx) => {
                // 이름을 지웠더라도 기본 7개 항목에 포함되므로 카운트에서 누락되지 않도록 주석 처리
                // if (!name || name.trim() === '') return false;
                const s = stocks[idx];
                const val = (s !== undefined && s !== null && s !== '' ? Number(s) : 0);
                return val < 1;
            }).length;

            const facRecord = store.facilityHistory?.[todayStr] || (pastDates.length > 0 ? store.facilityHistory?.[pastDates[0]] : null);
            const malfCount = facRecord ? (facRecord.items || []).filter(f => f.name && f.name.trim() !== '' && f.status && f.status !== 'normal').length : 0;

            const orderRecord = store.orderHistory?.[todayStr];
            const hasOrder = orderRecord?.stocks?.some(s => Number(s) > 0);

            const hasNewerReport = Object.keys(store.salesHistory || {}).some(d => d > todayStr) ||
                                  Object.keys(store.inventoryHistory || {}).some(d => d > todayStr) ||
                                  Object.keys(store.facilityHistory || {}).some(d => d > todayStr) ||
                                  Object.keys(store.orderHistory || {}).some(d => d > todayStr);

            // 이전의 유효한(0보다 큰) 가장 최근 매출액을 찾는 헬퍼 함수
            const getPastSalesBefore = (targetDateStr) => {
                for (const d of sortedDates) {
                    if (d < targetDateStr) {
                        const r = recordsMap.get(d);
                        const t = Number(r.total) || (Number(r.cash || 0) + Number(r.card || 0));
                        if (t > 0) return t;
                    }
                }
                return null;
            };

            const pastSalesForDay = getPastSalesBefore(todayStr);
            const pastSalesForPrev1 = getPastSalesBefore(prev1Str);
            const pastSalesForPrev2 = getPastSalesBefore(prev2Str);

            // 증감 비율 계산 (0원 이하 무시 및 가장 최근 과거 데이터 기준)
            const daySalesChange = pastSalesForDay !== null ? ((daySales - pastSalesForDay) / pastSalesForDay) * 100 : null;
            const prev1SalesChange = pastSalesForPrev1 !== null && prev1Sales > 0 ? ((prev1Sales - pastSalesForPrev1) / pastSalesForPrev1) * 100 : null;
            const prev2SalesChange = pastSalesForPrev2 !== null && prev2Sales > 0 ? ((prev2Sales - pastSalesForPrev2) / pastSalesForPrev2) * 100 : null;

            // '신규' 여부 판단: 올해 이전에 매출이 없었고 오늘 매출이 있는 경우 (매장별)
            const yearPrefixForNew = todayStr.split('-')[0];
            const hasPastSalesInYear = Array.from(recordsMap.entries()).some(([d, r]) => {
                const totalAmount = Number(r.total) || (Number(r.cash || 0) + Number(r.card || 0));
                return d.startsWith(yearPrefixForNew) && d < todayStr && totalAmount > 0;
            });
            const isNewStoreYearly = daySales > 0 && !hasPastSalesInYear;

            return {
                id: store.id, name: store.name, daySales, prev1Sales, prev2Sales, prev3Sales,
                daySalesChange, prev1SalesChange, prev2SalesChange, monthSales, yearSales, yearVariance,
                isDone, shortageCount, malfCount, hasOrder, isNew: isNewStoreYearly,
                isEdited: !hasNewerReport && (!!store.salesHistory?.[todayStr]?.isEdited || !!store.inventoryHistory?.[todayStr]?.isEdited || !!store.facilityHistory?.[todayStr]?.isEdited || !!store.orderHistory?.[todayStr]?.isEdited),
                todayStr
            };
        });
    }, [filteredStores, selectedDate]);

    // admin1, 2, 3 및 매니저 계정일 경우 선택 가능한 날짜 제한 (당일 포함 과거 3일)
    const allowedDates = useMemo(() => {
        const restrictedAdmins = ['admin1', 'admin2', 'admin3'];
        // 관리자 대상자 및 manager가 아닐 때 제한 해제
        if ((user?.role !== 'manager' && !restrictedAdmins.includes(user?.id)) || !currentDate) return null;
        
        const dates = [];
        const baseDate = new Date(currentDate);
        
        for (let i = 0; i < 3; i++) {
            const d = new Date(baseDate);
            d.setDate(baseDate.getDate() - i);
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            dates.push(`${y}-${m}-${day}`);
        }
        return dates;
    }, [user?.id, currentDate]);

    const years = useMemo(() => {
        if (!allowedDates) return ['2024', '2025', '2026'];
        return [...new Set(allowedDates.map(d => d.split('-')[0]))].sort();
    }, [allowedDates]);

    const months = useMemo(() => {
        if (!allowedDates) return Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
        return [...new Set(allowedDates
            .filter(d => d.startsWith(selectedDate.year))
            .map(d => d.split('-')[1]))].sort();
    }, [allowedDates, selectedDate.year]);

    const days = useMemo(() => {
        if (!allowedDates) return Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));
        return [...new Set(allowedDates
            .filter(d => d.startsWith(`${selectedDate.year}-${selectedDate.month}`))
            .map(d => d.split('-')[2]))].sort();
    }, [allowedDates, selectedDate.year, selectedDate.month]);

    const handleDateChange = (e) => {
        const { name, value } = e.target;
        const nextDate = { ...selectedDate, [name]: value };
        setSelectedDate(nextDate);
        
        // 날짜가 변경될 때 URL 파라미터를 업데이트하여 하단 네비게이션 등에서 유지되도록 함
        const dateStr = `${nextDate.year}-${nextDate.month}-${nextDate.day}`;
        navigate(`/dashboard?date=${dateStr}`, { replace: true });
    };

    const handleInventoryClick = (storeId) => {
        const todayStr = `${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`;
        navigate(`/store/${storeId}/inventory?date=${todayStr}`);
    };

    // 인쇄 리포트 통계 계산 함수들
    const calculateDailyTotal = (date) => {
        if (!filteredStores || !date) return { cash: 0, card: 0, total: 0, variance: 0, employeeCount: 0 };
        
        const normalizeDate = (dStr) => {
            if (!dStr) return '';
            const dt = dStr.split('T')[0];
            const [y, m, d] = dt.split('-');
            if (!y || !m || !d) return dt;
            return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
        };

        const targetDate = normalizeDate(date);
        let totals = { cash: 0, card: 0, total: 0, variance: 0, employeeCount: 0 };
        
        filteredStores.forEach(store => {
            const tempMap = new Map();
            if (store.salesHistory) {
                Object.entries(store.salesHistory).forEach(([dateKey, record]) => {
                    const recordDateStr = dateKey || record.localDate || (record.date ? record.date.split('T')[0] : null);
                    const d = normalizeDate(recordDateStr);
                    if (d) tempMap.set(d, record);
                });
            }
            if (store.salesData) {
                store.salesData.forEach(item => {
                    if (!item) return;
                    const d = normalizeDate(item.localDate || item.date);
                    if (d) tempMap.set(d, item);
                });
            }

            const targetRecord = tempMap.get(targetDate);
            if (targetRecord) {
                totals.cash += Number(targetRecord.cash) || 0;
                totals.card += Number(targetRecord.card) || 0;
                totals.total += Number(targetRecord.total) || 0;
                totals.variance += Number(targetRecord.variance) || 0;
                totals.employeeCount += Number(targetRecord.employeeCount) || 0;
            }
        });
        
        return totals;
    };

    const calculateMonthlyTotal = (date) => {
        if (!filteredStores || !date) return { cash: 0, card: 0, total: 0, variance: 0, employeeCount: 0 };
        
        // 날짜 정규화 헬퍼 (YYYY-MM-DD 보장)
        const normalizeDate = (dStr) => {
            if (!dStr) return '';
            const dt = dStr.split('T')[0];
            const [y, m, d] = dt.split('-');
            if (!y || !m || !d) return dt;
            return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
        };

        const yearMonth = normalizeDate(date).substring(0, 7); // YYYY-MM
        let totals = { cash: 0, card: 0, total: 0, variance: 0, employeeCount: 0 };
        
        filteredStores.forEach(store => {
            const tempMap = new Map();
            if (store.salesHistory) {
                Object.entries(store.salesHistory).forEach(([dateKey, record]) => {
                    const recordDateStr = dateKey || record.localDate || (record.date ? record.date.split('T')[0] : null);
                    const d = normalizeDate(recordDateStr);
                    if (d) tempMap.set(d, record);
                });
            }
            if (store.salesData) {
                store.salesData.forEach(item => {
                    if (!item) return;
                    const d = normalizeDate(item.localDate || item.date);
                    if (d) tempMap.set(d, item);
                });
            }

            tempMap.forEach((record, d) => {
                if (d.startsWith(yearMonth)) {
                    totals.cash += Number(record.cash) || 0;
                    totals.card += Number(record.card) || 0;
                    totals.total += Number(record.total) || 0;
                    totals.variance += Number(record.variance) || 0;
                    totals.employeeCount += Number(record.employeeCount) || 0;
                }
            });
        });
        
        return totals;
    };

    const calculateYearlyTotal = (date) => {
        if (!filteredStores || !date) return { cash: 0, card: 0, total: 0, variance: 0, employeeCount: 0 };
        
        const normalizeDate = (dStr) => {
            if (!dStr) return '';
            const dt = dStr.split('T')[0];
            const [y, m, d] = dt.split('-');
            if (!y || !m || !d) return dt;
            return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
        };

        const year = normalizeDate(date).substring(0, 4); // YYYY
        let totals = { cash: 0, card: 0, total: 0, variance: 0, employeeCount: 0 };
        
        filteredStores.forEach(store => {
            const tempMap = new Map();
            if (store.salesHistory) {
                Object.entries(store.salesHistory).forEach(([dateKey, record]) => {
                    const recordDateStr = dateKey || record.localDate || (record.date ? record.date.split('T')[0] : null);
                    const d = normalizeDate(recordDateStr);
                    if (d) tempMap.set(d, record);
                });
            }
            if (store.salesData) {
                store.salesData.forEach(item => {
                    if (!item) return;
                    const d = normalizeDate(item.localDate || item.date);
                    if (d) tempMap.set(d, item);
                });
            }

            tempMap.forEach((record, d) => {
                if (d.startsWith(year)) {
                    totals.cash += Number(record.cash) || 0;
                    totals.card += Number(record.card) || 0;
                    totals.total += Number(record.total) || 0;
                    totals.variance += Number(record.variance) || 0;
                    totals.employeeCount += Number(record.employeeCount) || 0;
                }
            });
        });
        
        return totals;
    };

    return (
        <Layout>

            <main className="dashboard-main-area">
                <div className="content-header">
                    <div className="header-top">
                        <h1>
                            {user?.role === 'admin' ? '전체 통합 대시보드' : `${user?.name} 운영 대시보드`}
                            <span style={{ fontSize: '0.7rem', opacity: 0.5, marginLeft: '10px' }}>v3.5.7</span>
                            {localStorage.getItem('baseball_hub_purge_version') && (
                                <span style={{ fontSize: '0.65rem', color: '#10b981', marginLeft: '8px', padding: '2px 6px', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '4px' }}>Purge OK</span>
                            )}
                            {user?.role === 'admin' && (
                                <span className="admin-user-tag">{user.id === 'admin4' ? '운영관리팀' : user.name}</span>
                            )}
                        </h1>
                        <div className={`date-selector`}>
                            <Calendar size={18} className="calendar-icon" />
                            <select
                                name="year"
                                value={selectedDate.year}
                                onChange={handleDateChange}
                                disabled={user?.role === 'manager' && window.innerWidth <= 768}
                            >
                                {years.map(y => <option key={y} value={y}>{y}년</option>)}
                            </select>
                            <select
                                name="month"
                                value={selectedDate.month}
                                onChange={handleDateChange}
                                disabled={user?.role === 'manager' && window.innerWidth <= 768}
                            >
                                {months.map(m => <option key={m} value={m}>{m}월</option>)}
                            </select>
                            <select
                                name="day"
                                value={selectedDate.day}
                                onChange={handleDateChange}
                            >
                                {days.map(d => <option key={d} value={d}>{d}일</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="header-bottom-row pc-only" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '10px' }}>
                        <p style={{ color: '#94a3b8', margin: 0 }}>
                            {user?.role === 'admin'
                                ? '50개 매장의 전체 운영 현황을 한눈에 파악하세요.'
                                : '현재 매장의 운영 및 보고 현황을 관리하세요.'}
                        </p>
                        
                        {/* 운영 형태 필터링 탭 (PC 전용) */}
                        {user?.role === 'admin' && selectedStoreId === 'all' && !['admin1', 'admin2', 'admin3'].includes(user?.id?.toLowerCase()) && (
                            <div className="operating-filter-tabs" style={{ 
                                display: 'flex', 
                                background: 'rgba(255, 255, 255, 0.05)', 
                                padding: '4px', 
                                borderRadius: '10px', 
                                border: '1px solid rgba(255, 255, 255, 0.1)' 
                            }}>
                                <button
                                    onClick={() => setFilterType('all')}
                                    style={{
                                        padding: '6px 16px',
                                        borderRadius: '8px',
                                        fontSize: '0.85rem',
                                        fontWeight: '700',
                                        border: 'none',
                                        cursor: 'pointer',
                                        background: filterType === 'all' ? '#38bdf8' : 'transparent',
                                        color: filterType === 'all' ? '#0f172a' : '#94a3b8',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    전체 매장
                                </button>
                                <button
                                    onClick={() => setFilterType('direct')}
                                    style={{
                                        padding: '6px 16px',
                                        borderRadius: '8px',
                                        fontSize: '0.85rem',
                                        fontWeight: '700',
                                        border: 'none',
                                        cursor: 'pointer',
                                        background: filterType === 'direct' ? '#38bdf8' : 'transparent',
                                        color: filterType === 'direct' ? '#0f172a' : '#94a3b8',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    직영 매장
                                </button>
                                <button
                                    onClick={() => setFilterType('rental')}
                                    style={{
                                        padding: '6px 16px',
                                        borderRadius: '8px',
                                        fontSize: '0.85rem',
                                        fontWeight: '700',
                                        border: 'none',
                                        cursor: 'pointer',
                                        background: filterType === 'rental' ? '#38bdf8' : 'transparent',
                                        color: filterType === 'rental' ? '#0f172a' : '#94a3b8',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    임대 매장
                                </button>
                            </div>
                        )}
                    </div>
                    {user?.role === 'admin' && (
                        <div className="mobile-only" style={{ marginTop: '20px' }}>
                            <div className="mobile-store-filter glass-card" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px', border: '1px solid rgba(56, 189, 248, 0.3)', background: 'rgba(30, 41, 59, 0.6)' }}>
                                <Store size={18} color="#38bdf8" />
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '2px', fontWeight: '600' }}>매장 필터</div>
                                    <select
                                        value={selectedStoreId}
                                        onChange={(e) => setSelectedStoreId(e.target.value)}
                                        style={{
                                            width: '100%',
                                            background: 'transparent',
                                            border: 'none',
                                            color: '#f8fafc',
                                            fontSize: '1rem',
                                            fontWeight: '700',
                                            outline: 'none',
                                            padding: 0,
                                            margin: 0,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {allowedStores
                                            .map(store => (
                                                <option key={store.id} value={store.id} style={{ background: '#1e293b' }}>{store.name}</option>
                                            ))
                                        }
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}


                    {/* Event Name Section */}
                    <div className="daily-event-section">
                        <div className="event-input-container glass-card" style={{ padding: '8px 20px', display: 'flex', alignItems: 'center', gap: '12px', border: '1px solid rgba(56, 189, 248, 0.2)' }}>
                            <div className="event-label-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#38bdf8', fontWeight: '700' }}>
                                <Trophy size={18} />
                                <span>행사명 ({['일', '월', '화', '수', '목', '금', '토'][new Date(`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`).getDay()]})</span>

                                {weather.temp !== null && (
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        marginLeft: '4px',
                                        padding: '2px 8px',
                                        background: 'rgba(255,255,255,0.05)',
                                        borderRadius: '6px',
                                        fontSize: '0.8rem',
                                        color: '#cbd5e1',
                                        border: '1px solid rgba(255,255,255,0.05)'
                                    }}>
                                        {getWeatherDisplay(weather.code).icon}
                                        <span>{weather.temp}°C</span>
                                    </div>
                                )}
                            </div>

                            <div className="event-inputs-wrapper" style={{ flex: 1, display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                                <div className="event-actions-wrapper" style={{ display: 'flex', gap: '10px', alignItems: 'center', flex: 2 }}>
                                            {user?.role === 'admin' ? (
                                                <>
                                                    <select
                                                        value={KBO_TEAMS.includes(eventHistory[`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`]) ? eventHistory[`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`] : (eventHistory[`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`] ? 'manual' : '')}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            if (val === 'manual') {
                                                                updateEvent(`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`, '');
                                                            } else {
                                                                updateEvent(`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`, val);
                                                            }
                                                        }}
                                                         disabled={user?.role !== 'admin'}
                                                        style={{
                                                            background: 'rgba(255, 255, 255, 0.05)',
                                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                                            borderRadius: '8px',
                                                            color: '#f8fafc',
                                                            padding: '6px 12px',
                                                            fontSize: '0.9rem',
                                                            outline: 'none',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        <option value="">행사 없음</option>
                                                        {KBO_TEAMS.map(team => (
                                                            <option key={team} value={team} style={{ background: '#1e293b' }}>{team}</option>
                                                        ))}
                                                        <option value="manual" style={{ background: '#1e293b' }}>직접 입력</option>
                                                    </select>

                                                    {(!KBO_TEAMS.includes(eventHistory[`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`]) && eventHistory[`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`] !== undefined) || (KBO_TEAMS.includes(eventHistory[`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`]) === false && eventHistory[`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`] !== '') ? (
                                                        <div style={{ flex: 1, position: 'relative' }}>
                                                            {/* 팀 리스트에 없는 경우에만 입력 칸 표시 (이미 선택된 팀이 아닐 때) */}
                                                            {!KBO_TEAMS.includes(eventHistory[`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`]) && (
                                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', width: '100%' }}>
                                                                    <Edit3 size={14} color="#64748b" style={{ flexShrink: 0, marginTop: '5px' }} />
                                                                    {user?.role === 'admin' ? (
                                                                        <input
                                                                            type="text"
                                                                            className="event-input-field"
                                                                            value={eventHistory[`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`] || ''}
                                                                            onChange={(e) => updateEvent(`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`, e.target.value)}
                                                                            placeholder="행사명을 직접 입력하세요"
                                                                            style={{
                                                                                flex: 1,
                                                                                background: 'transparent',
                                                                                border: 'none',
                                                                                color: '#f8fafc',
                                                                                fontSize: '1rem',
                                                                                outline: 'none',
                                                                                padding: '4px 0',
                                                                                borderBottom: '1px solid rgba(255,255,255,0.1)'
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <div style={{
                                                                            flex: 1,
                                                                            color: '#f8fafc',
                                                                            fontSize: '1rem',
                                                                            padding: '4px 0',
                                                                            borderBottom: '1px solid rgba(255,255,255,0.1)',
                                                                            wordBreak: 'keep-all',
                                                                            overflowWrap: 'break-word',
                                                                            lineHeight: '1.4',
                                                                            minHeight: '1.5rem',
                                                                            fontWeight: '500'
                                                                        }}>
                                                                            {eventHistory[`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`] || '행사 정보가 없습니다'}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : null}
                                                </>
                                            ) : (
                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', width: '100%', padding: '2px 0' }}>
                                                    <Edit3 size={14} color="#64748b" style={{ flexShrink: 0, marginTop: '4px' }} />
                                                    <div style={{ 
                                                        flex: 1, 
                                                        color: '#f8fafc', 
                                                        fontSize: '1rem', 
                                                        fontWeight: '500',
                                                        wordBreak: 'keep-all',
                                                        overflowWrap: 'break-word',
                                                        lineHeight: '1.4'
                                                    }}>
                                                        {eventHistory[`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`] || '등록된 행사가 없습니다'}
                                                    </div>
                                                </div>
                                            )}
                                </div>

                                {/* Attendance Input */}
                                <div className="attendance-info-box" style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.03)', padding: '4px 12px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)', flex: 1, minWidth: '150px' }}>
                                    <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: '600', whiteSpace: 'nowrap' }}>입장객 수:</span>
                                    {user?.role === 'admin' ? (
                                        <input
                                            type="number"
                                            className="attendance-input-field"
                                            value={attendanceHistory[`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`] || ''}
                                            onChange={(e) => updateAttendance(`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`, e.target.value)}
                                            placeholder="0"
                                            disabled={user?.id !== 'admin' && user?.id !== 'admin4'}
                                            style={{
                                                width: '100%',
                                                background: 'transparent',
                                                border: 'none',
                                                color: '#38bdf8',
                                                fontSize: '1rem',
                                                fontWeight: '700',
                                                outline: 'none',
                                                textAlign: 'right'
                                            }}
                                        />
                                    ) : (
                                        <span className="attendance-value-text" style={{ color: '#38bdf8', fontSize: '1rem', fontWeight: '700', flex: 1, textAlign: 'right' }}>
                                            {(attendanceHistory[`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`] || 0).toLocaleString()} 명
                                        </span>
                                    )}
                                    {user?.role === 'admin' && <span style={{ fontSize: '0.85rem', color: '#64748b' }}>명</span>}
                                </div>

                                {/* 매니저 전용 모바일 당일 매출 합계 복구 */}
                                {user?.role === 'manager' && window.innerWidth <= 768 && (
                                    <div className="manager-day-sales-box" style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '10px', 
                                        background: 'rgba(56, 189, 248, 0.05)', 
                                        padding: '10px 16px', 
                                        borderRadius: '12px', 
                                        border: '1px solid rgba(56, 189, 248, 0.15)', 
                                        flex: '1 1 100%',
                                        marginTop: '4px'
                                    }}>
                                        <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: '800', whiteSpace: 'nowrap' }}>오늘의 매출 합계:</span>
                                        <div style={{ flex: 1, textAlign: 'right' }}>
                                            <span style={{ color: '#38bdf8', fontSize: '1.2rem', fontWeight: '800' }}>
                                                ₩{stats.totalDaySales.toLocaleString()}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {user?.role === 'admin' && (
                                null
                            )}
                        </div>
                    </div>
                </div>

                {/* Stats Grid with Print Button */}
                {(user?.role === 'admin' || window.innerWidth >= 768) && (
                    <div className="sales-stats-wrapper" style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                        <div className="stats-grid" style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
                            <div className={`stat-card mobile-compact-card ${user?.role === 'admin' && !['admin1', 'admin2', 'admin3'].includes(user?.id?.toLowerCase()) ? '' : 'hide-on-mobile'}`}>
                                <span className="stat-label">
                                    {selectedStoreId === 'all' ? '당일 총 매출 합계' : `${filteredStores[0]?.name || '매장'} 당일 매출`}
                                </span>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                                    <div className="stat-value" style={{ color: '#38bdf8' }}>₩{stats.totalDaySales.toLocaleString()}</div>
                                    {stats.totalDaySales > 0 && stats.totalDaySalesChange !== null && (
                                        <div style={{ 
                                            fontSize: '0.85rem', 
                                            color: stats.totalDaySalesChange >= 0 ? '#22c55e' : '#ef4444',
                                            fontWeight: '700',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '2px'
                                        }}>
                                            {stats.totalDaySalesChange > 0 ? '▲' : (stats.totalDaySalesChange < 0 ? '▼' : '')}
                                            {Math.abs(stats.totalDaySalesChange) > 999 ? '999%+' : `${Math.abs(stats.totalDaySalesChange).toFixed(1)}%`}
                                        </div>
                                    )}
                                </div>
                                {/* 직영/임대 분담 - PC에서만 표시 */}
                                {user?.role === 'admin' && (
                                    <div className="operating-breakdown pc-only">
                                        <span>직영: ₩{stats.directDaySales.toLocaleString()}</span>
                                        <span>임대: ₩{stats.rentalDaySales.toLocaleString()}</span>
                                    </div>
                                )}
                                <div className="stat-variance"
                                     style={{ color: stats.totalDayVariance >= 0 ? '#38bdf8' : '#ef4444', fontSize: '0.9rem' }}>
                                        {user?.role === 'admin' ? '전일 대비 증감율 표시됨' : `(과부족: ${stats.totalDayVariance > 0 ? '+' : ''}${stats.totalDayVariance.toLocaleString()})`}
                                    </div>
                            </div>

                            <div className={`stat-card mobile-compact-card ${user?.id === 'admin' || user?.id === 'admin4' ? '' : 'hide-on-mobile'}`}>
                                <span className="stat-label">
                                    {selectedStoreId === 'all' ? '전일 총 매출 합계' : `${filteredStores[0]?.name || '매장'} 전일 매출`}
                                </span>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                                    <div className="stat-value" style={{ color: '#94a3b8' }}>₩{stats.totalPrev1Sales.toLocaleString()}</div>
                                    {stats.totalPrev1Sales > 0 && stats.totalPrev1SalesChange !== null && (
                                        <div style={{ 
                                            fontSize: '0.85rem', 
                                            color: stats.totalPrev1SalesChange >= 0 ? '#22c55e' : '#ef4444',
                                            fontWeight: '700',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '2px'
                                        }}>
                                            {stats.totalPrev1SalesChange > 0 ? '▲' : (stats.totalPrev1SalesChange < 0 ? '▼' : '')}
                                            {Math.abs(stats.totalPrev1SalesChange) > 999 ? '999%+' : `${Math.abs(stats.totalPrev1SalesChange).toFixed(1)}%`}
                                        </div>
                                    )}
                                </div>
                                {stats.totalPrev1Sales > 0 && stats.totalPrev1SalesChange !== null && stats.totalPastSalesForPrev1 > 0 && (
                                    <div className="stat-trend" style={{ 
                                        color: '#64748b', 
                                        fontSize: '0.8rem',
                                        display: 'flex',
                                        alignItems: 'baseline',
                                        gap: '6px'
                                    }}>
                                        과거 기준일 대비
                                        <span style={{ color: '#94a3b8', fontSize: '1rem', fontWeight: '700' }}>₩{stats.totalPastSalesForPrev1.toLocaleString()}</span>
                                    </div>
                                )}
                            </div>
                            <div className={`stat-card mobile-compact-card ${user?.id === 'admin' || user?.id === 'admin4' ? '' : 'hide-on-mobile'}`}>
                                <span className="stat-label">{selectedDate.month}월 누적 매출</span>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                                    <div className="stat-value" style={{ color: '#fbbf24' }}>₩{stats.totalMonthSales.toLocaleString()}</div>
                                {/* 직영/임대 분담 - PC에서만 표시 */}
                                {user?.role === 'admin' && (
                                    <div className="operating-breakdown pc-only">
                                        <span>직영: ₩{stats.directMonthSales.toLocaleString()}</span>
                                        <span>임대: ₩{stats.rentalMonthSales.toLocaleString()}</span>
                                    </div>
                                )}
                                <div className="stat-variance"
                                     style={{ color: stats.totalMonthVariance >= 0 ? '#fbbf24' : '#ef4444', fontSize: '0.9rem' }}>
                                        {user?.role === 'admin' ? `${selectedDate.year}년 누적` : `(과부족: ${stats.totalMonthVariance > 0 ? '+' : ''}${stats.totalMonthVariance.toLocaleString()})`}
                                    </div>
                                </div>
                            </div>
                            <div className={`stat-card mobile-compact-card ${user?.role === 'admin' && !['admin1', 'admin2', 'admin3'].includes(user?.id?.toLowerCase()) ? '' : 'hide-on-mobile'}`}>
                                <span className="stat-label">{selectedDate.year}년 전체 누적</span>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                                    <div className="stat-value" style={{ color: '#10b981' }}>₩{stats.totalYearSales.toLocaleString()}</div>
                                {/* 직영/임대 분담 - PC에서만 표시 */}
                                {user?.role === 'admin' && (
                                    <div className="operating-breakdown pc-only">
                                        <span>직영: ₩{stats.directYearSales.toLocaleString()}</span>
                                        <span>임대: ₩{stats.rentalYearSales.toLocaleString()}</span>
                                    </div>
                                )}
                                <div className="stat-variance"
                                     style={{ color: stats.totalYearVariance >= 0 ? '#10b981' : '#ef4444', fontSize: '0.9rem' }}>
                                        {user?.role === 'admin' ? '연간 총계' : `(과부족: ${stats.totalYearVariance > 0 ? '+' : ''}${stats.totalYearVariance.toLocaleString()})`}
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* 관리자 전용 인쇄 버튼 */}
                        {user?.role === 'admin' && (
                            <button
                                className="print-action-btn"
                                onClick={() => window.print()}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '10px 20px',
                                    background: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)',
                                    border: 'none',
                                    borderRadius: '12px',
                                    color: 'white',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s',
                                    fontSize: '0.95rem',
                                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                    whiteSpace: 'nowrap',
                                    height: 'fit-content'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                                onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                            >
                                <Printer size={18} />
                                인쇄하기
                            </button>
                        )}
                    </div>
                )}

                <div className="stats-grid status-cards-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
                    <div 
                        className="stat-card" 
                        style={{ padding: '15px', cursor: user?.role === 'admin' ? 'pointer' : 'default' }}
                        onClick={() => {
                            if (user?.role === 'admin') {
                                setDrillDownModal({
                                    isOpen: true,
                                    title: '보고 완료 매장 목록',
                                    stores: stats.completedStoresList,
                                    type: 'sales'
                                });
                            }
                        }}
                    >
                        <span className="stat-label">
                            {user?.role === 'admin' ? '보고 완료 매장' : '오늘의 보고 상태'}
                        </span>
                        <span className="stat-value" style={{ fontSize: '1.5rem' }}>
                            {user?.role === 'admin' ? (
                                `${stats.activeStores} / ${stats.totalCount}`
                            ) : (
                                stats.activeStores === 1 ? (
                                    <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                                        <CheckCircle size={18} /> 보고 완료
                                    </span>
                                ) : (
                                    <span style={{ color: '#94a3b8' }}>보고 대기</span>
                                )
                            )}
                        </span>
                    </div>
                    <div 
                        className="stat-card" 
                        style={{ padding: '15px', cursor: user?.role === 'admin' ? 'pointer' : 'default' }}
                        onClick={() => {
                            if (user?.role === 'admin') {
                                setDrillDownModal({
                                    isOpen: true,
                                    title: '재고 부족 매장 목록',
                                    stores: stats.shortageStores,
                                    type: 'inventory'
                                });
                            }
                        }}
                    >
                        <span className="stat-label">부족 재고 매장</span>
                        <span className="stat-value" style={{ fontSize: '1.5rem', color: stats.shortageCount > 0 ? '#ef4444' : '#10b981' }}>
                            {stats.shortageCount > 0 ? (
                                `부족 ${stats.shortageCount}개점 (${stats.totalShortageItems}건)`
                            ) : (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                                    <CheckCircle size={18} /> 정상
                                </span>
                            )}
                        </span>
                    </div>
                    <div 
                        className="stat-card" 
                        style={{ padding: '15px', cursor: user?.role === 'admin' ? 'pointer' : 'default' }}
                        onClick={() => {
                            if (user?.role === 'admin') {
                                setDrillDownModal({
                                    isOpen: true,
                                    title: '시설 결함/고장 매장 목록',
                                    stores: stats.malfunctionStores,
                                    type: 'facility'
                                });
                            }
                        }}
                    >
                        <span className="stat-label">시설 결함/고장</span>
                        <span className="stat-value" style={{ fontSize: '1.5rem', color: stats.malfunctionCount > 0 ? '#f97316' : '#10b981' }}>
                            {stats.malfunctionCount > 0 ? (
                                `${stats.malfunctionCount}개점 (${stats.totalMalfunctionItems}건)`
                            ) : (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                                    <CheckCircle size={18} /> 정상
                                </span>
                            )}
                        </span>
                    </div>
                    <div
                        className="stat-card"
                        style={{ padding: '15px', cursor: user?.role === 'admin' ? 'pointer' : 'default' }}
                        onClick={() => {
                            if (user?.role === 'admin') {
                                if (window.innerWidth <= 768) {
                                    setDrillDownModal({
                                        isOpen: true,
                                        title: '발주 요청 매장 목록',
                                        stores: stats.orderedStores,
                                        type: 'order'
                                    });
                                } else {
                                    setShowOrderModal(true);
                                }
                            }
                        }}
                    >
                        <span className="stat-label">발주 관리</span>
                        <span className="stat-value" style={{ fontSize: '1.5rem', color: stats.orderedCount > 0 ? '#38bdf8' : '#94a3b8' }}>
                            {stats.orderedCount > 0 ? (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                                    <CheckSquare size={18} /> 발주 있음
                                </span>
                            ) : (
                                '없음'
                            )}
                        </span>
                    </div>
                </div>

                {/* 최근 매장 보고 현황 (관리자 PC 전용) */}
                <div className="recent-activity-section hide-on-mobile" style={{ marginTop: '32px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h2 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#f8fafc', margin: 0 }}>최근 매장 보고 현황</h2>
                        <span style={{ fontSize: '0.8rem', color: '#64748b' }}>* 각 항목 클릭 시 상세 관리 페이지로 이동합니다</span>
                    </div>
                    
                    <div className="glass-card" style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', whiteSpace: 'nowrap' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                                    <th style={{ padding: '16px', color: '#94a3b8', fontSize: '0.85rem' }}>매장명</th>
                                    <th style={{ padding: '16px', color: '#94a3b8', fontSize: '0.85rem', textAlign: 'right' }}>전전일 매출</th>
                                    <th style={{ padding: '16px', color: '#94a3b8', fontSize: '0.85rem', textAlign: 'right' }}>전일 매출</th>
                                    <th style={{ padding: '16px', color: '#94a3b8', fontSize: '0.85rem', textAlign: 'right' }}>당일 매출</th>
                                    <th style={{ padding: '16px', color: '#94a3b8', fontSize: '0.85rem', textAlign: 'right' }}>{selectedDate.month}월 누계</th>
                                    <th style={{ padding: '16px', color: '#94a3b8', fontSize: '0.85rem', textAlign: 'right' }}>연간 누계</th>
                                    <th style={{ padding: '16px', color: '#94a3b8', fontSize: '0.85rem', textAlign: 'right' }}>과부족 (누계)</th> 
                                    <th style={{ padding: '16px', color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center' }}>재고</th>
                                    <th style={{ padding: '16px', color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center' }}>시설</th>
                                    <th style={{ padding: '16px', color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center' }}>발주</th>
                                    <th style={{ padding: '16px', color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center' }}>보고</th>
                                </tr>
                            </thead>
                            <tbody>
                                {storeDetails.map((store) => (
                                    <tr 
                                        key={store.id} 
                                        className="dashboard-table-row"
                                        style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', transition: 'background 0.2s' }}
                                    >
                                        <td style={{ padding: '16px', fontWeight: '700', color: '#f8fafc' }}>
                                            <div 
                                                style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                                                onClick={() => navigate(`/store/${store.id}/sales?date=${store.todayStr}`)}
                                                onMouseOver={(e) => e.currentTarget.style.color = '#38bdf8'}
                                                onMouseOut={(e) => e.currentTarget.style.color = '#f8fafc'}
                                            >
                                                {store.name}
                                                {store.isEdited && (
                                                    <span style={{ 
                                                        fontSize: '0.65rem', 
                                                        padding: '2px 4px', 
                                                        background: 'rgba(234, 179, 8, 0.2)', 
                                                        color: '#facc15', 
                                                        borderRadius: '4px',
                                                        fontWeight: '700',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        수정됨
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ padding: '16px', textAlign: 'right', color: '#94a3b8' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                <span>{store.prev2Sales > 0 ? `₩${store.prev2Sales.toLocaleString()}` : '-'}</span>
                                                {store.prev2SalesChange !== null && store.prev2Sales > 0 && (
                                                    <span style={{ 
                                                        fontSize: '0.75rem', 
                                                        color: store.prev2SalesChange >= 0 ? '#22c55e' : '#ef4444',
                                                        fontWeight: '600',
                                                        marginTop: '2px'
                                                    }}>
                                                        {store.prev2SalesChange > 0 ? '▲' : (store.prev2SalesChange < 0 ? '▼' : '')} 
                                                        {Math.abs(store.prev2SalesChange) > 999 ? '999%+' : `${Math.abs(store.prev2SalesChange).toFixed(1)}%`}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ padding: '16px', textAlign: 'right', color: '#cbd5e1' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                <span>{store.prev1Sales > 0 ? `₩${store.prev1Sales.toLocaleString()}` : '-'}</span>
                                                {store.prev1SalesChange !== null && store.prev1Sales > 0 && (
                                                    <span style={{ 
                                                        fontSize: '0.75rem', 
                                                        color: store.prev1SalesChange >= 0 ? '#22c55e' : '#ef4444',
                                                        fontWeight: '600',
                                                        marginTop: '2px'
                                                    }}>
                                                        {store.prev1SalesChange > 0 ? '▲' : (store.prev1SalesChange < 0 ? '▼' : '')} 
                                                        {Math.abs(store.prev1SalesChange) > 999 ? '999%+' : `${Math.abs(store.prev1SalesChange).toFixed(1)}%`}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td 
                                            style={{ padding: '16px', textAlign: 'right', color: '#38bdf8', fontWeight: '700', cursor: 'pointer' }}
                                            onClick={() => navigate(`/store/${store.id}/sales?date=${store.todayStr}`)}
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    {store.isNew && (
                                                        <span style={{ 
                                                            fontSize: '0.65rem', 
                                                            padding: '1px 4px', 
                                                            background: 'rgba(34, 197, 94, 0.2)', 
                                                            color: '#22c55e', 
                                                            borderRadius: '4px',
                                                            fontWeight: '800'
                                                        }}>
                                                            신규
                                                        </span>
                                                    )}
                                                    <span>{store.daySales > 0 ? `₩${store.daySales.toLocaleString()}` : '미입력'}</span>
                                                </div>
                                                {store.daySalesChange !== null && store.daySales > 0 && !store.isNew && (
                                                    <span style={{ 
                                                        fontSize: '0.75rem', 
                                                        color: store.daySalesChange >= 0 ? '#22c55e' : '#ef4444',
                                                        fontWeight: '600',
                                                        marginTop: '2px'
                                                    }}>
                                                        {store.daySalesChange > 0 ? '▲' : (store.daySalesChange < 0 ? '▼' : '')} 
                                                        {Math.abs(store.daySalesChange) > 999 ? '999%+' : `${Math.abs(store.daySalesChange).toFixed(1)}%`}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ padding: '16px', textAlign: 'right', color: '#cbd5e1' }}>
                                            ₩{store.monthSales.toLocaleString()}
                                        </td>
                                        <td style={{ padding: '16px', textAlign: 'right', color: '#cbd5e1' }}>
                                            ₩{store.yearSales.toLocaleString()}
                                        </td>
                                        {/* Added Variance Column */}
                                        <td style={{ padding: '16px', textAlign: 'right', color: store.yearVariance >= 0 ? '#cbd5e1' : '#ef4444', fontWeight: '700' }}>
                                            {store.yearVariance > 0 ? '+' : ''}{store.yearVariance.toLocaleString()}
                                        </td>
                                        <td style={{ padding: '16px', textAlign: 'center' }}>
                                            <span 
                                                onClick={() => navigate(`/store/${store.id}/inventory?date=${store.todayStr}`)}
                                                style={{ 
                                                    padding: '4px 8px', 
                                                    borderRadius: '4px', 
                                                    fontSize: '0.8rem', 
                                                    fontWeight: '700',
                                                    cursor: 'pointer',
                                                    background: store.shortageCount > 0 ? 'rgba(234, 179, 8, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                                                    color: store.shortageCount > 0 ? '#facc15' : '#34d399'
                                                }}
                                            >
                                                {store.shortageCount > 0 ? `부족 ${store.shortageCount}` : '정상'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '16px', textAlign: 'center' }}>
                                            <span 
                                                onClick={() => navigate(`/store/${store.id}/facility?date=${store.todayStr}`)}
                                                style={{ 
                                                    cursor: 'pointer',
                                                    color: store.malfCount > 0 ? '#f97316' : '#34d399',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '4px',
                                                    fontSize: '0.85rem'
                                                }}
                                            >
                                                {store.malfCount > 0 ? (
                                                    <><AlertTriangle size={14} /> {store.malfCount}건</>
                                                ) : (
                                                    <><CheckCircle size={14} /> 정상</>
                                                )}
                                            </span>
                                        </td>
                                        <td style={{ padding: '16px', textAlign: 'center' }}>
                                            <span 
                                                onClick={() => navigate(`/store/${store.id}/order?date=${store.todayStr}`)}
                                                style={{ 
                                                    cursor: 'pointer',
                                                    color: store.hasOrder ? '#38bdf8' : '#94a3b8',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '4px',
                                                    fontSize: '0.85rem'
                                                }}
                                            >
                                                {store.hasOrder ? <><CheckSquare size={14} /> 있음</> : '없음'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '16px', textAlign: 'center' }}>
                                            <span 
                                                style={{ 
                                                    padding: '4px 8px', 
                                                    borderRadius: '4px', 
                                                    fontSize: '0.8rem', 
                                                    fontWeight: '700',
                                                    background: store.isDone ? 'rgba(16, 185, 129, 0.2)' : 'rgba(234, 179, 8, 0.2)',
                                                    color: store.isDone ? '#34d399' : '#facc15'
                                                }}
                                            >
                                                {store.isDone ? '보고완료' : '진행중'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

            </main>
            {/* 발주 내역 상세 모달 */}
            {showOrderModal && (
                <div className="modal-overlay">
                    <div className="order-summary-modal">
                        {/* 인쇄 전용 별도 타이틀 영역 - 모달 내부 상단으로 이동 */}
                        <div className="print-only-title">
                            {selectedDate.year}. {selectedDate.month}. {selectedDate.day} 발주 통합 내역
                        </div>
                        <div className="modal-header">
                            <h2>
                                <ShoppingBag size={24} color="#38bdf8" />
                                {selectedDate.year}. {selectedDate.month}. {selectedDate.day} 발주 통합 내역
                            </h2>
                            <div className="modal-actions" style={{ display: 'flex', gap: '8px' }}>
                                <button
                                    className="icon-button"
                                    style={{ fontSize: '13px', padding: '6px 12px', background: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8', border: '1px solid rgba(56, 189, 248, 0.2)' }}
                                    onClick={() => {
                                        if (stats.orderedStores.length === 0) return showAlert('내보낼 당일 발주 내역이 없습니다.');
                                        const headers = ['날짜', '매장명', '품목명', '수량', '단위', '발주시간'];
                                        const dateStr = `${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`;
                                        const rows = [];
                                        stats.orderedStores.forEach(os => {
                                            os.items.forEach(item => {
                                                rows.push([
                                                    dateStr,
                                                    os.name,
                                                    item.name,
                                                    item.qty,
                                                    '박스',
                                                    os.updatedAt ? new Date(os.updatedAt).toLocaleTimeString('ko-KR') : '-'
                                                ]);
                                            });
                                        });
                                        const csvContent = ['\ufeff' + headers.join(','), ...rows.map(r => r.join(','))].join('\n');
                                        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                                        const link = document.createElement('a');
                                        link.href = URL.createObjectURL(blob);
                                        link.download = `${dateStr}_발주통합내역.csv`;
                                        link.click();
                                    }}
                                >
                                    <TrendingUp size={16} /> 당일 CSV
                                </button>
                                <button
                                    className="icon-button"
                                    style={{ fontSize: '13px', padding: '6px 12px', background: 'rgba(251, 191, 36, 0.1)', color: '#fbbf24', border: '1px solid rgba(251, 191, 36, 0.2)' }}
                                    onClick={() => {
                                        const headers = ['날짜', '매장명', '품목명', '수량', '단위', '발주시간'];
                                        const rows = [];
                                        const monthPrefix = `${selectedDate.year}-${selectedDate.month}`;

                                         allowedStores.forEach(store => {
                                            Object.entries(store.orderHistory || {}).forEach(([date, record]) => {
                                                if (date.startsWith(monthPrefix)) {
                                                    store.items.forEach((itemName, idx) => {
                                                        const qty = Number(record.stocks?.[idx]) || 0;
                                                        if (qty > 0) {
                                                            rows.push([
                                                                date,
                                                                store.name,
                                                                itemName,
                                                                qty,
                                                                '박스',
                                                                record.updatedAt ? new Date(record.updatedAt).toLocaleTimeString('ko-KR') : '-'
                                                            ]);
                                                        }
                                                    });
                                                }
                                            });
                                        });

                                        if (rows.length === 0) return showAlert(`${selectedDate.month}월에 기록된 발주 내역이 없습니다.`);
                                        rows.sort((a, b) => a[0].localeCompare(b[0]) || a[1].localeCompare(b[1]));

                                        const csvContent = ['\ufeff' + headers.join(','), ...rows.map(r => r.join(','))].join('\n');
                                        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                                        const link = document.createElement('a');
                                        link.href = URL.createObjectURL(blob);
                                        link.download = `${selectedDate.year}년_${selectedDate.month}월_월간통합발주내역.csv`;
                                        link.click();
                                    }}
                                >
                                    <ShoppingBag size={16} /> {Number(selectedDate.month)}월 전체 CSV
                                </button>
                                 <button className="print-button print-action-btn" onClick={() => window.print()} style={{ padding: '6px 12px' }}>
                                    <Printer size={18} /> 인쇄하기
                                </button>
                                <button className="close-button" onClick={() => setShowOrderModal(false)}>
                                    <X size={24} />
                                </button>
                            </div>
                        </div>

                        <div className="modal-content print-area">
                            {stats.orderedStores.length > 0 ? (
                                stats.orderedStores.map((os, idx) => (
                                    <div key={idx} className="store-order-card">
                                        <div className="store-order-header">
                                            <h3>{os.name}</h3>
                                            <span className="order-time">
                                                발주 시간: {os.updatedAt ? new Date(os.updatedAt).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }) : '기록 없음'}
                                            </span>
                                        </div>
                                        <table className="order-items-table">
                                            <thead>
                                                <tr>
                                                    <th>품목명</th>
                                                    <th>수량</th>
                                                    <th>단위</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {os.items.map((item, i) => (
                                                    <tr key={i}>
                                                        <td>{item.name}</td>
                                                        <td className="qty-cell">{item.qty}</td>
                                                        <td>박스</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {os.notes && os.notes.trim() && (
                                            <div className="order-notes" style={{
                                                marginTop: '12px',
                                                padding: '12px',
                                                background: 'rgba(56, 189, 248, 0.05)',
                                                border: '1px solid rgba(56, 189, 248, 0.2)',
                                                borderRadius: '8px',
                                                fontSize: '0.9rem',
                                                color: '#cbd5e1',
                                                lineHeight: '1.6'
                                            }}>
                                                <strong style={{ color: '#38bdf8', marginRight: '8px' }}>📝 요청사항:</strong>
                                                {os.notes}
                                            </div>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className="no-data-msg">금일 발주 내역이 있는 매장이 없습니다.</div>
                            )}
                        </div>
                        {/* 인쇄용 하단 푸터 (페이지 번호) */}
                        <div className="print-footer">
                            <div className="page-guide">페이지 가이드: 하단 여백에 시스템 페이지 번호가 표시됩니다.</div>
                            <div className="page-number"></div>
                        </div>
                    </div>
                </div>
            )}


            {/* 관리자 전용 인쇄 리포트 섹션 - 화면에서는 숨김, 인쇄 시에만 표시 (발주 모달이 닫혀 있을 때만 렌더링) */}
            {user?.role === 'admin' && !showOrderModal && (
                <div className="admin-wide-report print-report-section" style={{ marginTop: '32px', display: 'none' }}>
                    <div className="activities-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <Printer size={24} />
                                전체 매장 통합 리포트
                            </h3>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            {/* 인쇄 시에는 숨겨지는 날짜 확인용 안내 */}
                            <div className="desktop-only" style={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                                상단에서 선택한 날짜가 적용되었습니다.
                            </div>
                            <button
                                onClick={() => window.print()}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '10px 20px',
                                    background: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)',
                                    border: 'none',
                                    borderRadius: '12px',
                                    color: 'white',
                                    fontWeight: '700',
                                    cursor: 'pointer'
                                }}
                            >
                                <Printer size={18} />
                                인쇄하기
                            </button>
                        </div>
                    </div>

                    {/* 통계 카드 */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px', marginBottom: '32px' }}>
                        {/* 선택일 합계 */}
                        {(() => {
                            if (!selectedDate.year || !selectedDate.month || !selectedDate.day) return null;
                            const dailyTotal = calculateDailyTotal(`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`);
                            const todayDate = new Date(`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`);
                            const prev1Date = new Date(todayDate);
                            prev1Date.setDate(prev1Date.getDate() - 1);
                            const prev1Str = prev1Date.toISOString().split('T')[0];
                            const prev1Total = calculateDailyTotal(prev1Str).total;
                            
                            return (
                                <div style={{
                                    background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)',
                                    border: '1px solid rgba(56, 189, 248, 0.3)',
                                    borderRadius: '16px',
                                    padding: '24px'
                                }}>
                                    <div style={{ color: '#38bdf8', fontSize: '0.9rem', marginBottom: '8px', fontWeight: '600' }}>
                                        기준일: {`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`}
                                    </div>
                                    <div style={{ color: '#f8fafc', fontSize: '2rem', fontWeight: '800', marginBottom: '12px' }}>
                                        ₩{dailyTotal.total.toLocaleString()}
                                    </div>
                                    <div style={{ display: 'flex', gap: '12px', fontSize: '0.85rem', color: '#94a3b8', flexWrap: 'wrap', alignItems: 'center' }}>
                                        <span>합계: ₩{dailyTotal.total.toLocaleString()}</span>
                                        {prev1Total > 0 && (
                                            <span style={{ 
                                                color: dailyTotal.total >= prev1Total ? '#4ade80' : '#f87171',
                                                fontWeight: '700'
                                            }}>
                                                {dailyTotal.total > prev1Total ? '▲' : (dailyTotal.total < prev1Total ? '▼' : '')} 
                                                {Math.abs(((dailyTotal.total - prev1Total) / prev1Total) * 100).toFixed(1)}%
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', fontSize: '0.75rem', color: '#64748b', marginTop: '8px' }}>
                                        <span>현금: ₩{dailyTotal.cash.toLocaleString()}</span>
                                        <span>카드: ₩{dailyTotal.card.toLocaleString()}</span>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* 전일 합계 */}
                        {(() => {
                            if (!selectedDate.year || !selectedDate.month || !selectedDate.day) return null;
                            const todayDate = new Date(`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`);
                            const prev1Date = new Date(todayDate);
                            prev1Date.setDate(prev1Date.getDate() - 1);
                            const prev1Str = prev1Date.toISOString().split('T')[0];
                            const prev1Total = calculateDailyTotal(prev1Str);

                            const prev2Date = new Date(todayDate);
                            prev2Date.setDate(prev2Date.getDate() - 2);
                            const prev2Str = prev2Date.toISOString().split('T')[0];
                            const prev2Total = calculateDailyTotal(prev2Str).total;

                            return (
                                <div style={{
                                    background: 'rgba(148, 163, 184, 0.1)',
                                    border: '1px solid rgba(148, 163, 184, 0.3)',
                                    borderRadius: '16px',
                                    padding: '24px'
                                }}>
                                    <div style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: '8px', fontWeight: '600' }}>
                                        전일: {prev1Str}
                                    </div>
                                    <div style={{ color: '#f8fafc', fontSize: '1.5rem', fontWeight: '800', marginBottom: '12px' }}>
                                        ₩{prev1Total.total.toLocaleString()}
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', fontSize: '0.85rem', color: '#94a3b8', flexWrap: 'wrap', alignItems: 'center' }}>
                                        {prev2Total > 0 && (
                                            <span style={{ 
                                                color: prev1Total.total >= prev2Total ? '#4ade80' : '#f87171',
                                                fontWeight: '700'
                                            }}>
                                                {prev1Total.total > prev2Total ? '▲' : (prev1Total.total < prev2Total ? '▼' : '')} 
                                                {Math.abs(((prev1Total.total - prev2Total) / prev2Total) * 100).toFixed(1)}%
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })()}

                        {/* 전전일 합계 */}
                        {(() => {
                            if (!selectedDate.year || !selectedDate.month || !selectedDate.day) return null;
                            const todayDate = new Date(`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`);
                            const prev2Date = new Date(todayDate);
                            prev2Date.setDate(prev2Date.getDate() - 2);
                            const prev2Str = prev2Date.toISOString().split('T')[0];
                            const prev2Total = calculateDailyTotal(prev2Str);

                            const prev3Date = new Date(todayDate);
                            prev3Date.setDate(prev3Date.getDate() - 3);
                            const prev3Str = prev3Date.toISOString().split('T')[0];
                            const prev3Total = calculateDailyTotal(prev3Str).total;

                            return (
                                <div style={{
                                    background: 'rgba(148, 163, 184, 0.05)',
                                    border: '1px solid rgba(148, 163, 184, 0.2)',
                                    borderRadius: '16px',
                                    padding: '24px'
                                }}>
                                    <div style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '8px', fontWeight: '600' }}>
                                        전전일: {prev2Str}
                                    </div>
                                    <div style={{ color: '#f8fafc', fontSize: '1.5rem', fontWeight: '800', marginBottom: '12px' }}>
                                        ₩{prev2Total.total.toLocaleString()}
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', fontSize: '0.85rem', color: '#64748b', flexWrap: 'wrap', alignItems: 'center' }}>
                                        {prev3Total > 0 && (
                                            <span style={{ 
                                                color: prev2Total.total >= prev3Total ? '#4ade80' : '#f87171',
                                                fontWeight: '700'
                                            }}>
                                                {prev2Total.total > prev3Total ? '▲' : (prev2Total.total < prev3Total ? '▼' : '')} 
                                                {Math.abs(((prev2Total.total - prev3Total) / prev3Total) * 100).toFixed(1)}%
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })()}

                        {/* 월 누적 */}
                        {(() => {
                            if (!selectedDate.year || !selectedDate.month || !selectedDate.day) return null;
                            const monthlyTotal = calculateMonthlyTotal(`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`);
                            return (
                                <div style={{
                                    background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.15) 0%, rgba(236, 72, 153, 0.15) 100%)',
                                    border: '1px solid rgba(168, 85, 247, 0.3)',
                                    borderRadius: '16px',
                                    padding: '24px'
                                }}>
                                    <div style={{ color: '#a855f7', fontSize: '0.9rem', marginBottom: '8px', fontWeight: '600' }}>
                                        {selectedDate.month}월 누적 매출
                                    </div>
                                    <div style={{ color: '#f8fafc', fontSize: '2rem', fontWeight: '800', marginBottom: '12px' }}>
                                        ₩{monthlyTotal.total.toLocaleString()}
                                    </div>
                                    <div style={{ display: 'flex', gap: '12px', fontSize: '0.85rem', color: '#94a3b8', flexWrap: 'wrap' }}>
                                        <span>현금: ₩{monthlyTotal.cash.toLocaleString()}</span>
                                        <span>카드: ₩{monthlyTotal.card.toLocaleString()}</span>
                                        <span style={{ 
                                            color: monthlyTotal.variance < 0 ? '#f87171' : (monthlyTotal.variance > 0 ? '#4ade80' : '#94a3b8'),
                                            fontWeight: monthlyTotal.variance !== 0 ? '700' : 'normal'
                                        }}>
                                            과부족: {monthlyTotal.variance > 0 ? '+' : ''}₩{monthlyTotal.variance.toLocaleString()}
                                        </span>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* 년 누적 */}
                        {(() => {
                            if (!selectedDate.year || !selectedDate.month || !selectedDate.day) return null;
                            const yearlyTotal = calculateYearlyTotal(`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`);
                            return (
                                <div style={{
                                    background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(16, 185, 129, 0.15) 100%)',
                                    border: '1px solid rgba(34, 197, 94, 0.3)',
                                    borderRadius: '16px',
                                    padding: '24px'
                                }}>
                                    <div style={{ color: '#22c55e', fontSize: '0.9rem', marginBottom: '8px', fontWeight: '600' }}>
                                        {selectedDate.year}년 누적 매출
                                    </div>
                                    <div style={{ color: '#f8fafc', fontSize: '2rem', fontWeight: '800', marginBottom: '12px' }}>
                                        ₩{yearlyTotal.total.toLocaleString()}
                                    </div>
                                    <div style={{ display: 'flex', gap: '12px', fontSize: '0.85rem', color: '#94a3b8', flexWrap: 'wrap' }}>
                                        <span>현금: ₩{yearlyTotal.cash.toLocaleString()}</span>
                                        <span>카드: ₩{yearlyTotal.card.toLocaleString()}</span>
                                        <span style={{ 
                                            color: yearlyTotal.variance < 0 ? '#f87171' : (yearlyTotal.variance > 0 ? '#4ade80' : '#94a3b8'),
                                            fontWeight: yearlyTotal.variance !== 0 ? '700' : 'normal'
                                        }}>
                                            과부족: {yearlyTotal.variance > 0 ? '+' : ''}₩{yearlyTotal.variance.toLocaleString()}
                                        </span>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>

                    {/* 매장별 매출 테이블 */}
                    <div className="activities-list" style={{ background: 'rgba(30, 41, 59, 0.4)', borderRadius: '16px', overflow: 'hidden' }}>
                        <div className="activity-item header-item" style={{
                            display: 'grid',
                            gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr',
                            background: 'rgba(255, 255, 255, 0.03)',
                            padding: '14px 24px',
                            fontWeight: '700',
                            color: '#94a3b8',
                            fontSize: '0.85rem'
                        }}>
                            <div>매장명</div>
                            <div style={{ textAlign: 'right' }}>현금</div>
                            <div style={{ textAlign: 'right' }}>카드</div>
                            <div style={{ textAlign: 'right' }}>합계</div>
                            <div style={{ textAlign: 'center' }}>과부족</div>
                        </div>

                        {allowedStores && allowedStores.map((store) => {
                            const salesData = store.salesHistory?.[`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`];
                            return (
                                <div key={store.id} className="activity-item row-item" style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr',
                                    padding: '16px 24px',
                                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                                    alignItems: 'center'
                                }}>
                                    <div style={{ fontWeight: '600', color: '#f8fafc' }}>{store.name}</div>
                                    {salesData ? (
                                        <>
                                            <div style={{ textAlign: 'right', color: '#cbd5e1' }}>₩{salesData.cash.toLocaleString()}</div>
                                            <div style={{ textAlign: 'right', color: '#cbd5e1' }}>₩{salesData.card.toLocaleString()}</div>
                                            <div style={{ textAlign: 'right', fontWeight: '700', color: '#38bdf8' }}>₩{salesData.total.toLocaleString()}</div>
                                            <div style={{ 
                                                textAlign: 'center', 
                                                color: salesData.variance < 0 ? '#f87171' : (salesData.variance > 0 ? '#4ade80' : '#94a3b8')
                                            }}>
                                                {salesData.variance > 0 ? '+' : ''}{salesData.variance.toLocaleString()}
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div style={{ textAlign: 'right', color: '#64748b' }}>-</div>
                                            <div style={{ textAlign: 'right', color: '#64748b' }}>-</div>
                                            <div style={{ textAlign: 'right', color: '#64748b', fontSize: '0.85rem' }}>미입력</div>
                                            <div style={{ textAlign: 'center', color: '#64748b' }}>-</div>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            {/* 드릴다운 매장 목록 모달 */}
            {drillDownModal.isOpen && (
                <div className="modal-overlay" onClick={() => setDrillDownModal({ ...drillDownModal, isOpen: false })}>
                    <div className="store-drilldown-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{drillDownModal.title}</h2>
                            <button className="close-button" onClick={() => setDrillDownModal({ ...drillDownModal, isOpen: false })}>
                                <X size={24} />
                            </button>
                        </div>
                        <div className="drilldown-list">
                            {drillDownModal.stores.length > 0 ? (
                                drillDownModal.stores.map(store => (
                                    <div
                                        key={store.id}
                                        className="drilldown-item"
                                        onClick={() => {
                                            const todayStr = `${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`;
                                            navigate(`/store/${store.id}/${drillDownModal.type}?date=${todayStr}`);
                                            setDrillDownModal({ ...drillDownModal, isOpen: false });
                                        }}
                                    >
                                        <span className="drilldown-item-name">{store.name}</span>
                                        {store.count !== undefined && (
                                            <span className="drilldown-item-badge" style={{
                                                backgroundColor: drillDownModal.type === 'inventory' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(249, 115, 22, 0.1)',
                                                color: drillDownModal.type === 'inventory' ? '#ef4444' : '#f97316'
                                            }}>
                                                {store.count}건
                                            </span>
                                        )}
                                        <ChevronRight size={18} color="#64748b" />
                                    </div>
                                ))
                            ) : (
                                <div className="no-data-msg" style={{ padding: '20px' }}>해당하는 매장이 없습니다.</div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default Dashboard;
